import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Chip, 
  Paper, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  IconButton,
  InputAdornment,
  useTheme,
  CircularProgress,
  Alert,
  TablePagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  Search as SearchIcon,
  Add as AddIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { whitelistService, chargersService, companiesService, projectService } from '../services/api';
import { WhitelistEntry, RfidFormData, ProjectRfidCardRequest, UpdateProjectRfidCardRequest, DeleteProjectRfidCardRequest } from '../types';
import AddRfidCardModal from '../components/AddRfidCardModal';

// RFID Access interface
interface RFIDAccess {
  id: string;
  rfid: string;
  customer: string;
  name: string;
  pricing: string;
  email: string;
  addedDate: string;
  lastUsed: string;
  unit_id: string;
  serial_number: string;
  company_id: number | null;
  is_active: boolean;
}

const transformWhitelistEntries = (entries: WhitelistEntry[]): RFIDAccess[] =>
  entries.map((entry) => ({
    id: entry.rfid,
    rfid: entry.rfid,
    customer: entry.customer || '',
    name: entry.user_name,
    pricing: 'N/A',
    email: entry.email,
    addedDate: new Date(entry.created_at).toLocaleDateString(),
    lastUsed: new Date(entry.updated_at).toLocaleString(),
    unit_id: entry.unit_id || '',
    serial_number: entry.serial_number || 'NA',
    company_id: entry.company_id,
    is_active: entry.is_active ?? true,
  }));

const AccessControl: React.FC = () => {
  const theme = useTheme();
  const { orgId, projectId } = useParams<{ orgId: string; projectId: string }>();
  const [searchTerm, setSearchTerm] = useState('');
  const [whitelistData, setWhitelistData] = useState<RFIDAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingEntry, setEditingEntry] = useState<RFIDAccess | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isRfidDisabled, setIsRfidDisabled] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<RFIDAccess | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectAssetNames, setProjectAssetNames] = useState<string[]>([]);

  // Fetch project name and assets to check if RFID should be disabled
  useEffect(() => {
    const loadProjectContext = async () => {
      if (!orgId || !projectId) {
        return;
      }

      try {
        const [assetsResponse, projectInfoResponse] = await Promise.all([
          chargersService.getProjectAssets(projectId, orgId),
          projectService.getProjectInfo(projectId, orgId),
        ]);
        
        if (assetsResponse.success && assetsResponse.data && assetsResponse.data.assets) {
          const assets = assetsResponse.data.assets;
          setProjectAssetNames(
            Array.from(
              new Set(
                assets
                  .map((asset) => asset.unit_name)
                  .filter((unitName): unitName is string => Boolean(unitName))
              )
            )
          );

          // Check if any unit_name starts with "KE", "HB", "BC", or "BP"
          const hasRestrictedUnit = assets.some((asset) => {
            const unitName = asset.unit_name || '';
            return unitName.startsWith('KE') || 
                   unitName.startsWith('HB') || 
                   unitName.startsWith('BC') || 
                   unitName.startsWith('BP');
          });
          
          setIsRfidDisabled(hasRestrictedUnit);
        }

        if (projectInfoResponse.success && projectInfoResponse.data?.project_name) {
          setProjectName(projectInfoResponse.data.project_name);
        }
      } catch (err) {
        console.error('Failed to load project context:', err);
        // On error, don't disable the button (fail open)
      }
    };

    loadProjectContext();
  }, [orgId, projectId]);

  // Fetch whitelist data from API
  useEffect(() => {
    const fetchWhitelistData = async () => {
      if (!orgId || !projectId) {
        setError('Missing organization ID or project ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching project whitelist data for orgId:', orgId, 'projectId:', projectId);
        const response = await whitelistService.getProjectWhitelist(projectId, orgId);
        
        if (response.success && response.data) {
          const transformedData = transformWhitelistEntries(response.data.entries);
          console.log('Project whitelist data loaded:', transformedData);
          setWhitelistData(transformedData);
        } else {
          console.error('No project whitelist data found');
          setWhitelistData([]);
        }
      } catch (err) {
        console.error('Failed to load project whitelist data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load project whitelist data');
        setWhitelistData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWhitelistData();
  }, [orgId, projectId]);

  const filteredData = whitelistData.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.rfid.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.customer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resolveCompanyId = async (companyName: string): Promise<number | null> => {
    if (!orgId) {
      setError('Missing organization ID');
      return null;
    }

    const companiesResponse = await companiesService.getCompanies(orgId);
    if (!companiesResponse.success || !companiesResponse.data) {
      setError('Failed to fetch companies');
      return null;
    }

    const company = companiesResponse.data.companies.find(
      (c) => c.company_name === companyName
    );

    if (!company) {
      setError('Company not found');
      return null;
    }

    const companyId = Number(company.id);
    if (isNaN(companyId)) {
      setError('Invalid company ID');
      return null;
    }

    return companyId;
  };

  const refreshWhitelist = async () => {
    if (!orgId || !projectId) return;

    const updatedResponse = await whitelistService.getProjectWhitelist(projectId, orgId);
    if (updatedResponse.success && updatedResponse.data) {
      setWhitelistData(transformWhitelistEntries(updatedResponse.data.entries));
    }
  };

  const handleAddRfidCard = async (rfidData: RfidFormData) => {
    if (!orgId || !projectId) {
      setError('Missing organization ID or project ID');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const companyId = await resolveCompanyId(rfidData.company || '');
      if (companyId === null) {
        return;
      }

      const projectRfidRequest: ProjectRfidCardRequest = {
        org_id: orgId,
        project_id: projectId,
        rfid: rfidData.rfid,
        user_name: rfidData.user_name,
        email: rfidData.email,
        company_id: companyId,
      };
      
      console.log('Adding RFID card to project:', projectRfidRequest);
      const response = await whitelistService.addProjectRfidCard(projectRfidRequest);
      
      if (response.success) {
        console.log('RFID card added successfully');
        await refreshWhitelist();
        handleCloseModal();
      } else {
        setError('Failed to add RFID card');
      }
    } catch (err) {
      console.error('Failed to add RFID card:', err);
      setError(err instanceof Error ? err.message : 'Failed to add RFID card');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateRfidCard = async (rfidData: RfidFormData) => {
    if (!orgId || !projectId || !editingEntry) {
      setError('Missing organization ID, project ID, or entry to edit');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const companyId = await resolveCompanyId(rfidData.company || '');
      if (companyId === null) {
        return;
      }

      // Omit unit_id / serial_number so the API updates all project rows for this RFID
      const updateRequest: UpdateProjectRfidCardRequest = {
        org_id: orgId,
        project_id: projectId,
        rfid: editingEntry.rfid,
        user_name: rfidData.user_name,
        email: rfidData.email,
        company_id: companyId,
        is_active: editingEntry.is_active,
      };

      console.log('Updating RFID card:', updateRequest);
      const response = await whitelistService.updateProjectRfidCard(updateRequest);

      if (response.success) {
        console.log('RFID card updated successfully');
        await refreshWhitelist();
        handleCloseModal();
      } else {
        setError('Failed to update RFID card');
      }
    } catch (err) {
      console.error('Failed to update RFID card:', err);
      setError(err instanceof Error ? err.message : 'Failed to update RFID card');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenModal = () => {
    setModalMode('create');
    setEditingEntry(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (entry: RFIDAccess) => {
    setModalMode('edit');
    setEditingEntry(entry);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalMode('create');
    setEditingEntry(null);
  };

  const getAssetsForDeleteDialog = (rfid: string): string[] => {
    const serialNumbers = whitelistData
      .filter((item) => item.rfid === rfid)
      .map((item) => item.serial_number)
      .filter((serialNumber) => serialNumber && serialNumber !== 'NA');

    const uniqueSerialNumbers = Array.from(new Set(serialNumbers));
    if (uniqueSerialNumbers.length > 0) {
      return uniqueSerialNumbers;
    }

    return projectAssetNames;
  };

  const handleOpenDeleteDialog = (entry: RFIDAccess) => {
    setEntryToDelete(entry);
  };

  const handleCloseDeleteDialog = () => {
    if (isDeleting) return;
    setEntryToDelete(null);
  };

  const handleDeleteRfidCard = async () => {
    if (!orgId || !projectId || !entryToDelete) {
      setError('Missing organization ID, project ID, or entry to delete');
      return;
    }

    try {
      setIsDeleting(true);
      setError(null);

      const deleteRequest: DeleteProjectRfidCardRequest = {
        org_id: orgId,
        project_id: projectId,
        rfid: entryToDelete.rfid,
      };

      const response = await whitelistService.deleteProjectRfidCard(deleteRequest);

      if (response.success) {
        await refreshWhitelist();
        setEntryToDelete(null);
      } else {
        setError('Failed to delete RFID card');
      }
    } catch (err) {
      console.error('Failed to delete RFID card:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete RFID card');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ flexGrow: 1, backgroundColor: theme.palette.background.default, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error && !isModalOpen && !entryToDelete) {
    return (
      <Box sx={{ flexGrow: 1, backgroundColor: theme.palette.background.default, p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: theme.palette.background.default, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          RFID Access
        </Typography>
        <Tooltip title={isRfidDisabled ? "RFID administration is not yet available for this project" : ""}>
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              disabled={isRfidDisabled}
              sx={{
                backgroundColor: theme.palette.primary.light,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                },
                '&:disabled': {
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              }}
            >
              ADD RFID CARD
            </Button>
          </span>
        </Tooltip>
      </Box>
      <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 4 }}>
        This is the RFID Access page. Here you can manage user permissions and access rights.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search Section */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 3 }}>
        <TextField
          placeholder="Search by name, RFID, email, or company..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0); // Reset to first page when searching
          }}
          size="small"
          sx={{ 
            width: 400,
            '& .MuiOutlinedInput-root': {
              height: '40px',
              borderRadius: '4px',
            },
            '& .MuiInputBase-input': {
              height: '40px',
              padding: '0 14px',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* RFID Access Table */}
      <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>RFID</TableCell>
              <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Pricing</TableCell>
              <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Added Date</TableCell>
              <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Last Used</TableCell>
              <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}> </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No whitelist entries available
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                      {row.rfid}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                      {row.customer}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      {row.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.pricing}
                      size="small"
                      sx={{
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      {row.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      {row.addedDate}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ScheduleIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {row.lastUsed}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={isRfidDisabled ? "RFID administration is not yet available for this project" : "Edit"}>
                        <span>
                          <IconButton
                            size="small"
                            sx={{ color: '#1976D2' }}
                            onClick={() => handleOpenEditModal(row)}
                            disabled={isRfidDisabled}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={isRfidDisabled ? "RFID administration is not yet available for this project" : "Delete"}>
                        <span>
                          <IconButton
                            size="small"
                            sx={{ color: '#F44336' }}
                            onClick={() => handleOpenDeleteDialog(row)}
                            disabled={isRfidDisabled}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ 
          borderTop: '1px solid rgba(0,0,0,0.12)',
          '& .MuiTablePagination-toolbar': {
            paddingLeft: 0,
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.6)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-selectLabel': {
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.6)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-displayedRows': {
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.87)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-select': {
            fontFamily: 'Roboto',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.87)',
            letterSpacing: '0.3px',
            lineHeight: '19px'
          }
        }}
      />

      {/* Add / Edit RFID Card Modal */}
      <AddRfidCardModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={modalMode === 'edit' ? handleUpdateRfidCard : handleAddRfidCard}
        isLoading={isSubmitting}
        orgId={orgId || ''}
        unitId="520e4ddd-50b9-4a20-98fb-2443a087305d"
        mode={modalMode}
        initialData={
          editingEntry
            ? {
                rfid: editingEntry.rfid,
                serial_number: editingEntry.serial_number,
                user_name: editingEntry.name,
                email: editingEntry.email,
                company: editingEntry.customer,
              }
            : undefined
        }
      />

      <Dialog open={Boolean(entryToDelete)} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete RFID</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete RFID "
            <Box component="span" sx={{ fontWeight: 700 }}>
              {entryToDelete?.rfid}
            </Box>
            " for project "
            <Box component="span" sx={{ fontWeight: 700 }}>
              {projectName || projectId}
            </Box>
            " and includes the following assets: "
            <Box component="span" sx={{ fontWeight: 700 }}>
              {entryToDelete
                ? getAssetsForDeleteDialog(entryToDelete.rfid).join(', ') || 'none'
                : ''}
            </Box>
            "
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteRfidCard}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccessControl;
